import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import React from 'react'
import Sheet from 'components/utils/Sheet'
import { FullTrip } from 'tripPlanner/types/common'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import BodyTextBlock from '../../../LuxKit/TextBlocks/BodyTextBlock'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { FormattedMessage } from 'react-intl'

interface Props {
  trip: FullTrip
}
function TripLandingPageHeader(props: Props) {
  const isTripChatPageEnabled = useOptimizelyExperiment(OptimizelyFeatureFlags.trpTripChatPage)
  const deviceType = useAppSelector(state => state.system.device.deviceType)
  const isDesktop = deviceType !== 'mobile' && deviceType !== 'tablet'
  const showAITripPlanner = isTripChatPageEnabled && isDesktop

  const { trip } = props
  const daysUntilNextTrip = trip.startDate ? daysUntilDate(trip.startDate.toDate()) : null

  const firstDestination = trip.destinationsGeo?.[0]
  const firstDestinationName = firstDestination
    ? `${firstDestination.shortName}, ${firstDestination.countryCode}`
    : ''

  const numberOfAdults = trip.travellerRooms?.[0]?.adults
  const numberOfChildren = trip.travellerRooms?.[0]?.childAges?.length
  const hasChildren = !!(numberOfChildren && numberOfChildren > 0)

  const renderTravellers = () => {
    if (hasChildren) {
      return (
        /* eslint-disable-next-line formatjs/no-multiple-plurals -- ICU supports multiple plurals, needed for proper RTL support */
        <FormattedMessage
          id="oKPR/Hg70x"
          defaultMessage="{adults, plural, one {# adult} other {# adults}}, {children, plural, one {# child} other {# children}}"
          description="Traveller count with adults and children"
          values={{
            adults: numberOfAdults,
            children: numberOfChildren,
          }}
        />
      )
    }
    return (
      <FormattedMessage
        id="dz4pBReW4g"
        defaultMessage="{adults, plural, one {# adult} other {# adults}}"
        description="Traveller count with adults only"
        values={{
          adults: numberOfAdults,
        }}
      />
    )
  }

  return (
    <Sheet
      paletteType="inverse"
      style={{ position: 'relative' }}
    >
      <Group direction="horizontal" gap={24} verticalAlign="center" horizontalAlign="space-between">
        <Group direction="vertical" gap={16}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {!!daysUntilNextTrip && daysUntilNextTrip > 0 && (
              <>
                <StatusLabel
                  variant="confirmation"
                >
                  <FormattedMessage
                    id="0iYGpnr/rO"
                    defaultMessage="{count, plural, one {# Day} other {# Days}} to go"
                    description="Days until trip countdown label"
                    values={{ count: daysUntilNextTrip }}
                  />
                </StatusLabel>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="6fr1IdFhRp"
                    defaultMessage="until your next trip"
                    description="Text indicating until next trip"
                  />
                </BodyText>
              </>
            )}
          </Group>
          <HeadingTextBlock variant="heading1">
            <Heading variant="heading1">
              {trip.name}
            </Heading>
          </HeadingTextBlock>
          <Group direction="horizontal" gap="4 16" wrap="wrap">
            {firstDestinationName && (
              <BodyTextBlock variant="large" startIcon={<LineMapMarkerIcon />}>
                {firstDestinationName}
              </BodyTextBlock>
            )}
            {trip.startDate && trip.endDate && (
              <BodyTextBlock variant="large" startIcon={<LineCalendarIcon />}>
                <FormatDateTimeRange startValue={trip.startDate} endValue={trip.endDate} format="short-day-name-day-month-year" />
              </BodyTextBlock>
            )}
            {!!numberOfAdults && (
              <BodyTextBlock variant="large" startIcon={<LineUsersAltIcon />}>
                {renderTravellers()}
              </BodyTextBlock>
            )}
          </Group>
          <Group direction="horizontal" gap={16} verticalAlign="center">
            <TextButton
              kind="primary"
              size="large"
              shape="round"
              to={`/trip-planner/trip/${trip.id}/itinerary`}
            >
              <FormattedMessage
                id="Z8T7Zjgp71"
                defaultMessage="View trip plan"
                description="Button text to view trip plan"
              />
            </TextButton>
            <TextButton
              kind="secondary"
              size="large"
              startIcon={<SolidSparklesIcon />}
              shape="round"
              to="/trip-planner/generate-trip"
            >
              <FormattedMessage
                id="REu0c5bOgz"
                defaultMessage="Plan new trip"
                description="Button text to plan a new trip"
              />
            </TextButton>
          </Group>
        </Group>
        {showAITripPlanner && (
          <Sheet padding={24} background="neutral-eight" borderRadius="L" paletteType="default">
            <Group direction="vertical" gap={4}>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary">
                <FormattedMessage
                  id="6r9C9Xnzye"
                  defaultMessage="You've been invited to try our"
                  description="Text for invitation to try new feature"
                />
              </BodyText>
              <Group direction="horizontal" gap={12}>
                <Heading variant="heading3" colour="neutral-one">
                  <FormattedMessage
                    id="/lVpvIxVpi"
                    defaultMessage="New AI TripPlanner"
                    description="Heading for new AI TripPlanner feature"
                  />
                </Heading>
                <StatusLabel variant="confirmation">
                  <FormattedMessage
                    id="s+BLJEDYp4"
                    defaultMessage="Beta"
                    description="Beta label for new feature"
                  />
                </StatusLabel>
              </Group>
              <Group direction="vertical" gap={8}>
                <BodyText variant="medium">
                  Chat with your personal travel designer who will help<br></br>craft the perfect itinerary for you.
                </BodyText>
                <TextButton kind="primary" size="large" to="/trip-planner/chat-trip-planner">
                  <FormattedMessage
                    id="V3UG3oegBt"
                    defaultMessage="Give it a go"
                    description="Button text for trying the chat trip planner"
                  />
                </TextButton>
              </Group>
            </Group>
          </Sheet>
        )}
      </Group>
    </Sheet>
  )
}

export default TripLandingPageHeader
